import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { ClarityModule } from '@clr/angular';
import { rxResource } from '@angular/core/rxjs-interop';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { FormsModule } from '@angular/forms';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { first } from 'rxjs';
import dayjs from 'dayjs';
import { ToastService } from 'src/app/services/toast.service';
import { Row } from 'src/app/models/ui/table/row';
import { ConfirmService } from 'src/app/services/confirm.service';

type LogLevel = '0' | '1';

interface LogEintrag {
  id: number;
  datum: Date;
  level: LogLevel;
  text: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-logbuch',
  imports: [CommonModule, ClarityModule, FormsModule, MrTranslatePipe, TablePrettyPrintPipe, HideIDColumnsPipe],
  templateUrl: './logbuch.component.html',
  styleUrl: './logbuch.component.scss',
})
export class LogbuchComponent {
  protected authService = inject(AuthService);
  private mrTranslate = inject(MrTranslatePipe);
  private toastr = inject(ToastService);
  private confirmService = inject(ConfirmService);

  public readonly ostammid = input.required<number>();
  private readonly apiService = inject(APIService);
  private readonly logbuchResource = rxResource({
    params: this.ostammid,
    stream: ({ params: ostammid }) => this.apiService.getAnlagenLogbuch(ostammid),
  });
  protected readonly logbuch = computed(() => this.logbuchResource.value());
  protected readonly logbuchLoading = this.logbuchResource.isLoading;
  protected readonly selectedRow = signal<Row>(undefined);

  nowFormat: string = 'YYYY-MM-DDTHH:mm:ss';

  id: number = -1;

  neuerText = '';
  neuesLevel: LogLevel = '0';

  logs: LogEintrag[] = [];

  cancel() {
    this.selectedRow.set(null);
    this.id = -1;
    this.neuerText = '';
    this.neuesLevel = '0';
  }

  addEintrag() {
    this.selectedRow.set(null);
    this.id = 0;
    this.neuerText = '';
    this.neuesLevel = '0';
  }

  speichern() {
    if (!this.neuerText.trim()) {
      this.toastr.error(this.mrTranslate.transform('Bitte Text eintragen'));
      return;
    }

    let tosend = {
      status: 'edit',
      ostammid: this.ostammid(),
      logdatum: dayjs().format(this.nowFormat),
      logstatus: parseInt(this.neuesLevel),
      logtext: this.neuerText,
      id: this.id,
    };

    this.apiService
      .setAnlagenLogbuch(tosend)
      .pipe(first())
      .subscribe(({ success, error }: any) => {
        if (success == true) {
          this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));

          this.id = -1;
          this.neuerText = '';
          this.neuesLevel = '0';

          this.logbuchResource.reload();
        } else this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
      });
  }

  async loeschen(index: number) {
    const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie diesen Eintrag löschen möchten?');
    const ok = await this.confirmService.danger(text);
    if (ok) {
      let tosend = {
        status: 'loeschen',
        ostammid: this.ostammid(),
        logdatum: dayjs().format(this.nowFormat),
        logstatus: parseInt(this.neuesLevel),
        logtext: this.neuerText,
        id: this.selectedRow().id,
      };

      this.apiService
        .setAnlagenLogbuch(tosend)
        .pipe(first())
        .subscribe(({ success, error }: any) => {
          if (success == true) {
            this.toastr.warning(this.mrTranslate.transform('Erfolgreich gelöscht!'));

            this.selectedRow.set(null);
            this.id = -1;
            this.neuerText = '';
            this.neuesLevel = '0';

            this.logbuchResource.reload();
          } else this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
        });
    }
  }
}
